import { StackNavigationProp, StackScreenProps } from "@react-navigation/stack";
import { ScrollView, StyleSheet, View } from "react-native";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { MissionAction, useMissionHandler } from "../../hooks/missions";
import React, { use, useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import { useI18n } from "../../hooks/i18n-hooks";
import { ContractState, TerminateReason } from "../../__generated__/graphql";
import { CompositeNavigationProp, useNavigation } from "@react-navigation/native";
import { BottomTabNavigationProp } from "@react-navigation/bottom-tabs";
import { WorkerTabParamList } from "../../navigations/worker/Home";
import { ClientTabParamList } from "../../navigations/client/Home";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { GET_MISSION, GET_TERMINATION_INFO } from "../../graphql/core";
import VStack from "../../ui_atoms/VStack";
import { useTheme } from "../../hooks/use-theme";
import { SwipeButton } from "../../components/SwipeButton";
import { useSnackbar } from "../../hooks/snackbar";
import { useTracking } from "../../hooks/tracking";
import TextField from "../../components/TextField";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import Button from "../../components/Button";
import MarkdownView from "../../components/MarkdownView";
import { useIntercom } from "../../hooks/intercom";
import DateField from "../../components/DateField";
import Text from "../../components/Text";
import Gap from "../../ui_atoms/Gap";
import { SnackbarInfo } from "../../atoms/snackbar";
import { formatDate } from "../../utils";
import { parseISO } from "date-fns";

type FormData = {
    otherReason: string;
    terminationDate: Date;
};

type MissionTerminationProps = StackScreenProps<RootStackParamList, "MissionTermination">;

type LocalNavigatorProps = CompositeNavigationProp<
    StackNavigationProp<RootStackParamList, "Home">,
    CompositeNavigationProp<
        BottomTabNavigationProp<WorkerTabParamList, "MissionsScreen">,
        BottomTabNavigationProp<ClientTabParamList, "MissionsScreen">
    >
>;

const MissionTerminationScreen = ({ route }: MissionTerminationProps) => {
    const navigation = useNavigation<LocalNavigatorProps>();
    const i18n = useI18n();
    const missionId = route?.params?.missionId || "test-mission-id";
    const { profileType } = use(ProfileSwitcherManagerContext);
    const { trackEvent } = useTracking();
    const { colors } = useTheme();

    const {
        data,
        error: getError,
        loading,
    } = useQuery(GET_MISSION, {
        variables: { id: missionId },
    });
    const {
        data: termInfo,
        error: getInfoError,
        loading: getInfoLoading,
    } = useQuery(GET_TERMINATION_INFO, {
        variables: { missionId },
    });

    const goAfterAction = useCallback(() => {
        if (navigation.canGoBack()) {
            navigation.goBack();
        } else {
            navigation.replace("Home", {
                screen: "MissionsScreen",
                params: { screen: "MissionList" },
            });
        }
    }, [navigation]);
    const { showSnackbar } = useSnackbar();

    const onActionSuccess = useCallback(
        (actionName: MissionAction) => {
            void trackEvent("click_confirm_terminate_mission");

            if (actionName == "close") {
                goAfterAction();
                return;
            }
            let text = i18n.t(`screens.termination.success_info.${actionName}`);
            let mode: SnackbarInfo["mode"] = "info";

            if (actionName === "terminate") {
                // we need to keep the old translation for app compatibility
                text = i18n.t(`screens.termination.success_info.request`);
                mode = "success";
            }
            showSnackbar({
                text,
                mode,
            });
            navigation.popToTop();
        },
        [goAfterAction, i18n, navigation, showSnackbar, trackEvent],
    );

    const {
        onAction,
        processingAction,
        error: procError,
        reset,
    } = useMissionHandler(onActionSuccess);
    const onClearError = useCallback(() => {
        reset();
        navigation.replace("Home", { screen: "MissionsScreen", params: { screen: "MissionList" } });
    }, [navigation, reset]);

    const {
        control,
        handleSubmit,
        formState: { isValid },
    } = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
    });

    const mission = data?.getMission || null;
    const error = useMemo(() => {
        if (procError) {
            return i18n.t(`screens.termination.action_errors.${processingAction || "default"}`);
        }
        if (getError) {
            return i18n.t(`screens.termination.get_errors`);
        }
        if (getInfoError) {
            return i18n.t(`screens.termination.get_info_errors`);
        }
        return null;
    }, [procError, getInfoError, getError, i18n, processingAction]);

    const styles = useStyleSheet();

    const newState = termInfo?.getTerminationInfo?.newState;
    const cancellation = termInfo?.getTerminationInfo?.cancellationRequest;
    const { presentIntercom } = useIntercom();

    useEffect(() => {
        if (newState === ContractState.Terminated) {
            navigation.setOptions({ title: i18n.t("contract.terminate_contract") });
        } else {
            navigation.setOptions({ title: i18n.t("contract.cancel_contract") });
        }
    }, [i18n, navigation, newState]);

    const buttonTitle = useMemo(() => {
        if (
            cancellation ||
            newState === ContractState.Terminated ||
            newState === ContractState.Cancelled
        ) {
            return i18n.t("screens.termination.actions.request");
        }
        return i18n.t(`screens.termination.actions.close`);
    }, [i18n, newState, cancellation]);

    const buttonAction = useMemo(() => {
        if (cancellation) {
            return "close";
        }
        if (newState === ContractState.Terminated || newState === ContractState.Cancelled) {
            return "terminate";
        }
        return "close";
    }, [newState, cancellation]);

    const onSupportPressed = useCallback(() => {
        void presentIntercom();
    }, [presentIntercom]);

    const onSubmit = useCallback<SubmitHandler<FormData>>(
        (data) => {
            if (mission) {
                void onAction(
                    buttonAction,
                    mission,
                    TerminateReason.Other,
                    data.terminationDate,
                    data.otherReason,
                );
            }
        },
        [buttonAction, mission, onAction],
    );

    return (
        <>
            <LoadingBoundary
                loading={loading || getInfoLoading}
                error={error}
                clearError={onClearError}
            >
                <ScrollView>
                    {cancellation ? (
                        <VStack gap="2/3" style={styles.container} top={1}>
                            <Text style={styles.terminationTextLabel}>
                                {i18n.t(`screens.termination.termination_reason`)}
                            </Text>
                            <Text
                                style={[
                                    styles.terminationTextLabel,
                                    { color: colors.greyAccessible },
                                ]}
                            >
                                {termInfo.getTerminationInfo.cancellationRequest?.otherReason}
                            </Text>
                            <Gap gap="1/3" />
                            <Text style={styles.terminationTextLabel}>
                                {i18n.t(`screens.termination.termination_date_wish`)}
                            </Text>
                            {termInfo.getTerminationInfo.minimalEndDate && (
                                <Text
                                    style={[
                                        styles.terminationTextLabel,
                                        { color: colors.greyAccessible },
                                    ]}
                                >
                                    {formatDate(
                                        parseISO(termInfo.getTerminationInfo.minimalEndDate),
                                    )}
                                </Text>
                            )}
                        </VStack>
                    ) : (
                        mission && (
                            <VStack gap={1} style={styles.container} top={1}>
                                <MarkdownView
                                    markdown={i18n.t(
                                        `screens.termination.callouts.${profileType}.request`,
                                    )}
                                />
                                <Controller
                                    name="otherReason"
                                    control={control}
                                    rules={{
                                        required: true,
                                        validate: (v) =>
                                            !v
                                                ? i18n.t("screens.termination.missing_other_reason")
                                                : true,
                                    }}
                                    render={({
                                        field: { onChange, value, onBlur },
                                        fieldState: { error },
                                    }) => {
                                        return (
                                            <TextField
                                                multiline
                                                value={value}
                                                onChangeText={onChange}
                                                onBlur={onBlur}
                                                label={i18n.t(`screens.termination.reason_label`)}
                                                error={error?.message}
                                            />
                                        );
                                    }}
                                />

                                {newState === ContractState.Terminated && (
                                    <MarkdownView
                                        markdown={i18n.t(
                                            `screens.termination.select_termination_date`,
                                        )}
                                    />
                                )}
                                {newState === ContractState.Terminated && (
                                    <Controller
                                        name="terminationDate"
                                        control={control}
                                        rules={{
                                            required: true,
                                            validate: (v) =>
                                                !v
                                                    ? i18n.t("screens.termination.missing_date")
                                                    : true,
                                        }}
                                        render={({
                                            field: { onChange, value, onBlur },
                                            fieldState: { error },
                                        }) => {
                                            return (
                                                <DateField
                                                    value={value}
                                                    onChange={onChange}
                                                    onBlur={onBlur}
                                                    minimumDate={new Date()}
                                                    label={i18n.t(
                                                        `screens.termination.select_date`,
                                                    )}
                                                    error={error?.message}
                                                />
                                            );
                                        }}
                                    />
                                )}
                            </VStack>
                        )
                    )}
                </ScrollView>
                <View style={styles.containerActions}>
                    {cancellation ? (
                        <Button
                            buttonMode="secondary"
                            label={i18n.t("screens.termination.actions.support")}
                            onPress={onSupportPressed}
                            size="large"
                        />
                    ) : (
                        <SwipeButton
                            testID="button-terminate-mission"
                            disabled={!isValid || !mission || !newState}
                            processing={!!processingAction}
                            title={buttonTitle}
                            onSwipeSuccess={handleSubmit(onSubmit)}
                            thumbIconName="swipe-right"
                            mode="danger"
                        />
                    )}
                </View>
            </LoadingBoundary>
        </>
    );
};

const useStyleSheet = () => {
    const { gridSize, fonts } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                terminationTextLabel: {
                    ...fonts.bodyMedium,
                },
                container: {
                    paddingHorizontal: gridSize,
                },
                containerActions: {
                    marginVertical: gridSize,
                    paddingHorizontal: gridSize,
                    position: "relative",
                    alignContent: "center",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    width: "100%",
                    alignItems: "center",
                },
            }),
        [fonts.bodyMedium, gridSize],
    );
};
export default MissionTerminationScreen;
